// ══════════════════════════════════════════════════════════════════
// realtime.js — La Conciergerie
// Canal WebSocket Supabase Realtime + suspension caméra
// Extrait du Script 1 de index.html (migration Option A → Option B Vite)
// ══════════════════════════════════════════════════════════════════

// Note : supaClient est exposé sur window.supaClient par supabase.js
// realtimeBus est utilisé par useSt (dans supabase.js) via window.__lcRealtimeBus

// ══════════════ REALTIME BUS (events Supabase Realtime → useSt) ══════════════
// Un seul canal WebSocket écoute TOUS les changements de la table app_data.
// Chaque useSt s'abonne au realtimeBus pour son propre 'change:<key>' event.
// Avantage : 1 canal partagé au lieu de N canaux (N = ~18 instances de useSt au boot).
import { photoLog } from './utils.js';
export const realtimeBus=(typeof window!=='undefined')?(window.__lcRealtimeBus=window.__lcRealtimeBus||new EventTarget()):new EventTarget();

// Santé du canal Realtime — consommé par le polling de useSt (supabase.js).
// false par défaut : tant que SUBSCRIBED n'est pas confirmé, le polling assure la sync.
// Passe à true sur SUBSCRIBED, retombe à false sur erreur/timeout/close/suspension caméra.
if(typeof window!=='undefined'&&window.__lcRealtimeHealthy===undefined)window.__lcRealtimeHealthy=false;

// Démarrage du canal Realtime. On le fait une seule fois, idempotent.
// Si supaClient n'a pas pu être créé (CDN down), on log et on s'abstient : le polling 20s prend le relais.
export function startRealtimeChannel(){
  if(typeof window==='undefined')return;
  if(window.__lcRealtimeStarted)return; // déjà démarré dans cette page
  if(!window.supaClient){
    window.__lcRealtimeHealthy=false;
    console.warn('[Realtime] supaClient absent, canal non démarré (le polling 60s assure la sync)');
    return;
  }
  window.__lcRealtimeStarted=true;
  try{
    const ch=window.supaClient
      .channel('app_data_changes')
      .on('postgres_changes',{event:'*',schema:'public',table:'app_data'},(payload)=>{
        try{
          // INSERT/UPDATE → payload.new contient {key, value, updated_at}
          // DELETE → payload.old (rare ici, on n'efface jamais de clés)
          const row=payload?.new||payload?.old;
          if(!row||!row.key)return;
          // On dispatche un event 'change:<key>' que chaque useSt(k) écoute.
          // detail.value = la nouvelle valeur déjà décodée (ou null pour DELETE).
          realtimeBus.dispatchEvent(new CustomEvent('change:'+row.key,{
            detail:{key:row.key,value:payload?.new?.value??null,eventType:payload.eventType}
          }));
          console.log('[Realtime] change reçu pour',row.key,'(',payload.eventType,')');
        }catch(e){
          console.error('[Realtime] Erreur traitement payload',e,payload);
        }
      })
      .subscribe((status,err)=>{
        if(status==='SUBSCRIBED'){
          window.__lcRealtimeHealthy=true;
          console.log('[Realtime] Canal app_data → SUBSCRIBED ✓ (polling 60s mis en veille)');
        }else if(status==='CHANNEL_ERROR'||status==='TIMED_OUT'||status==='CLOSED'){
          window.__lcRealtimeHealthy=false;
          console.warn('[Realtime] Canal app_data → '+status+' (polling 60s repris)',err||'');
        }
      });
    window.__lcRealtimeChannel=ch;
  }catch(e){
    console.error('[Realtime] Échec démarrage canal :',e);
    window.__lcRealtimeStarted=false; // on autorise un retry futur
    window.__lcRealtimeHealthy=false; // le polling doit reprendre la main
  }
}
// ⚠️ PAS d'appel à startRealtimeChannel() ici — voir le bloc en bas de fichier.
// Historique (corrigé le 23/07/2026) : ce module était appelé deux fois au chargement
// (ici et en fin de fichier), mais `supabase.js` IMPORTE `realtime.js` (sa ligne 9).
// En ES modules, une dépendance s'exécute intégralement AVANT le module importateur :
// les deux appels tombaient donc systématiquement avant la création de `window.supaClient`
// (supabase.js ligne ~125) → sortie immédiate sur le garde `if(!window.supaClient)`,
// et le canal Realtime ne démarrait JAMAIS depuis la migration Vite.
// Symptôme console : deux « supaClient absent » suivis de « SDK Supabase chargé »,
// aucun « SUBSCRIBED ✓ », et le polling assurant seul toute la synchronisation.

// ─── Suspension/reprise du canal Realtime (sécurité caméra) ───
// Sur certains Android (ex. Xiaomi/MIUI), quand l'appareil photo natif passe au premier plan,
// le système suspend l'onglet Chrome. Un WebSocket Realtime ouvert peut faire échouer la
// restauration de l'onglet au retour (→ « sortie de l'appli »). On ferme donc le canal le temps
// de la prise de photo, puis on le rouvre. Le polling 20s assure la sync pendant ce court laps.
export function suspendRealtime(){
  try{
    if(window.__lcRealtimeChannel&&window.supaClient){
      window.supaClient.removeChannel(window.__lcRealtimeChannel);
      window.__lcRealtimeChannel=null;
      window.__lcRealtimeStarted=false;
      window.__lcRealtimeHealthy=false; // le polling reprend la main pendant la photo
      if(typeof photoLog==='function')photoLog('Realtime suspendu (caméra)');
    }
  }catch(e){if(typeof photoLog==='function')photoLog('Suspension Realtime échouée',(e&&e.message)||String(e));}
}
export function resumeRealtime(){
  try{startRealtimeChannel();if(typeof photoLog==='function')photoLog('Realtime relancé');}
  catch(e){if(typeof photoLog==='function')photoLog('Reprise Realtime échouée',(e&&e.message)||String(e));}
}

// ══════════════ DÉMARRAGE DIFFÉRÉ DU CANAL ══════════════
// `window.supaClient` est créé par supabase.js, qui importe ce module : il n'existe donc
// pas encore à cet instant précis (voir le bloc explicatif plus haut). On ne peut pas
// appeler startRealtimeChannel() directement — il faut attendre que le client existe.
//
// Stratégie : microtâche d'abord (suffit dans le cas nominal, supabase.js finit son
// évaluation juste après), puis sondage court en filet de sécurité si le SDK vendor
// tarde. Abandon au bout de 10s : le polling de useSt prend alors le relais, ce qui
// est exactement le comportement de secours prévu.
function startRealtimeWhenReady(){
  if(typeof window==='undefined')return;
  if(window.__lcRealtimeBootstrapped)return; // idempotent (HMR, double import)
  window.__lcRealtimeBootstrapped=true;
  let waited=0;
  const STEP_MS=100;
  const MAX_WAIT_MS=10000;
  const attempt=()=>{
    if(window.__lcRealtimeStarted)return; // déjà démarré entre-temps
    if(window.supaClient){startRealtimeChannel();return;}
    waited+=STEP_MS;
    if(waited>=MAX_WAIT_MS){
      window.__lcRealtimeHealthy=false;
      console.warn('[Realtime] supaClient toujours absent après 10s — canal abandonné, le polling assure la sync');
      return;
    }
    setTimeout(attempt,STEP_MS);
  };
  // Microtâche : laisse supabase.js terminer son évaluation avant le premier essai.
  Promise.resolve().then(attempt);
}
startRealtimeWhenReady();
